/**
 * PHARMAKON — the pharmacy app shell (design/ux/01_foundations.md §6.1; WP-18)
 *
 * DOM order: skip link → topbar → rail → main → status bar (the topbar is
 * BEFORE the rail so the search is the 2nd/3rd Tab stop, AUD-NAV-07; the CSS
 * grid keeps the rail on the left and spanning every row).
 *
 * Rail mode by viewport (§5.1, §6.4): expanded 232 px from 1280 px (unless
 * the user collapsed it), compact 88 px from 1024 to 1279 px whatever the
 * persisted choice, a modal drawer below 1024 px (NavDrawerHost).
 *
 * Also owned here: the window title from the navigation tree (§6.3,
 * AUD-NAV-23), the location context the PageHeader breadcrumb reads, the
 * notification poll, the alertes-équipe hosts, the display-preferences panel
 * and the keyboard layer (WP-17), the « nouvelle fenêtre » question and the band of a window opened
 * by it (X XWP-74), the follow-up of a user picked by PIN on a team workstation (X XWP-72). The single
 * ToastHost is mounted at App level.
 */
import * as React from "react";
import { useLocation } from "react-router-dom";
import { Topbar } from "@/components/layout/Topbar.tsx";
import { TweaksPanel } from "@/components/layout/TweaksPanel.tsx";
import { ShellStatusBar } from "@/components/layout/ShellStatusBar.tsx";
import { RailNav } from "@/components/layout/nav/RailNav.tsx";
import { NavDrawerHost, focusNavToggle } from "@/components/layout/nav/NavDrawer.tsx";
import { ShellNavContext } from "@/components/layout/nav/ShellNavContext";
import { useNavModel } from "@/components/layout/nav/useNavModel";
import BetaBanner from "@/components/layout/BetaBanner.jsx";
import { LicenceBanner } from "@/components/domain/Domain.tsx";
// WP-17 — the single keyboard dispatcher, the F1 sheet and the palette.
import { KeyboardLayer } from "@/components/keyboard/KeyboardLayer.tsx";
// Alertes équipe (PLAN_mobile_pharma_owner_manager §3.5) — DEUX montages, et il
// faut les deux : `AlerteBannieresSlot` dans <main> pour que le bandeau « info »
// défile avec le contenu, `AlerteHost` au niveau du shell pour les couches
// fixes (toast persistant, surcouche bloquante) et le sondage.
import { AlerteHost, AlerteBannieresSlot } from "@/components/alertes";
// Trésorerie TWP-22 — the receiving toast of a counter transfer (UX 02 §13.4): the caissier hears it
// on whatever page he works (the sale screen most of the time), from his own comptoir's frames.
import { ReceptionComptoir } from "@/components/tresorerie/caissier/ReceptionComptoir.jsx";
// X XWP-74 — « ouvrir dans une nouvelle fenêtre »: the one question before opening, and the band of a
// window opened that way (« Retourner vers la fenêtre précédente », Alt+W).
import { OuvrirFenetreDialog } from "@/components/shell/OuvrirFenetreDialog.jsx";
import { RetourFenetrePrecedente } from "@/components/shell/RetourFenetrePrecedente.jsx";
// X XWP-72 — a user picked by PIN on a team workstation: prolonged while active, back to the picker
// when idle, the owner's password step-up (inert on a password session).
import SuiviSessionEquipe from "@/components/equipe/SuiviSessionEquipe.jsx";
import { useThemeStore } from "@/stores/themeStore.js";
import { useNavStore, forPath } from "@/stores/navStore.js";
import {
  APP_DEFAULT_TITLE,
  APP_TITLE,
  BREAKPOINTS,
  RAIL_MODES,
  SHELLS,
} from "@/config/ui.config.js";
import { useMediaQuery } from "@/hooks/useMediaQuery.js";
import { windowTitleFor } from "@/lib/nav-tree.js";
import { useNotificationStore } from "@/stores/index.js";

const DRAWER_QUERY = `(max-width: ${BREAKPOINTS.DESKTOP_SMALL - 1}px)`;
const COMPACT_QUERY = `(max-width: ${BREAKPOINTS.DESKTOP - 1}px)`;

export default function AppShell({ children }) {
  const sidebarCollapsed = useThemeStore((s) => s.sidebarCollapsed);
  const mobileNavOpen = useThemeStore((s) => s.mobileNavOpen);
  const closeMobileNav = useThemeStore((s) => s.closeMobileNav);
  const startPoll = useNotificationStore((s) => s.startPoll);
  const stopPoll = useNotificationStore((s) => s.stopPoll);
  const location = useLocation();
  const model = useNavModel();
  const documentLabel = useNavStore((s) => s.documentLabel);
  const titleOverride = useNavStore((s) => s.titleOverride);
  const setDocumentLabel = useNavStore((s) => s.setDocumentLabel);
  const requestFlyout = useNavStore((s) => s.requestFlyout);

  const drawer = useMediaQuery(DRAWER_QUERY);
  const narrow = useMediaQuery(COMPACT_QUERY);
  const railMode = drawer
    ? RAIL_MODES.DRAWER
    : narrow || sidebarCollapsed
      ? RAIL_MODES.COMPACT
      : RAIL_MODES.EXPANDED;

  React.useEffect(() => {
    startPoll?.();
    return () => stopPoll?.();
  }, [startPoll, stopPoll]);

  // Navigating closes the mobile drawer — otherwise the destination page opens
  // underneath it.
  React.useEffect(() => {
    closeMobileNav?.();
  }, [location.pathname, closeMobileNav]);

  // Leaving the drawer layout (window widened) closes it.
  React.useEffect(() => {
    if (!drawer) closeMobileNav?.();
  }, [drawer, closeMobileNav]);

  // Escape dismisses the drawer and gives focus back to the hamburger (the
  // drawer's own focus trap handles it first when focus is inside).
  React.useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        closeMobileNav?.();
        focusNavToggle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileNavOpen, closeMobileNav]);

  // Window title from the tree (§6.3): « {page} · {catégorie} — Pharmakon »,
  // a detail page « {document} — {page} — Pharmakon ».
  const doc = forPath(documentLabel, location.pathname)?.label ?? null;
  const override = forPath(titleOverride, location.pathname)?.title ?? null;
  const title = override
    ? `${override} — ${APP_TITLE}`
    : (windowTitleFor(model.active, doc, APP_TITLE) ?? APP_DEFAULT_TITLE);
  React.useEffect(() => {
    document.title = title;
  }, [title]);

  const shellNav = React.useMemo(
    () => ({
      pathname: location.pathname,
      mode: model.mode,
      active: model.active,
      requestFlyout,
      setDocumentLabel,
    }),
    [location.pathname, model.mode, model.active, requestFlyout, setDocumentLabel],
  );

  return (
    <ShellNavContext.Provider value={shellNav}>
      <a href="#main" className="skip-link">
        Aller au contenu
      </a>
      <div
        className="app-grid"
        data-shell={SHELLS.PHARMACY}
        data-rail={railMode}
        data-sidebar={sidebarCollapsed ? "collapsed" : "expanded"}
        data-mobile-nav={mobileNavOpen ? "open" : "closed"}
      >
        <Topbar />
        <aside
          className="pk-sidebar pk-rail"
          data-mode={railMode}
          style={{ gridArea: "sidebar" }}
          aria-hidden={railMode === RAIL_MODES.DRAWER && !mobileNavOpen ? true : undefined}
        >
          {railMode === RAIL_MODES.DRAWER ? (
            <NavDrawerHost />
          ) : (
            <RailNav
              compact={railMode === RAIL_MODES.COMPACT}
              canCollapse={!narrow}
            />
          )}
        </aside>
        <main id="main" tabIndex={-1} style={{ gridArea: "main" }} className="overflow-y-auto bg-bg">
          <RetourFenetrePrecedente />
          <BetaBanner />
          <LicenceBanner />
          <AlerteBannieresSlot />
          {children}
        </main>
        <ShellStatusBar />
      </div>
      {mobileNavOpen && drawer && (
        <button
          type="button"
          className="pk-nav-scrim"
          data-shell={SHELLS.PHARMACY}
          aria-label="Fermer le menu"
          tabIndex={-1}
          onClick={() => {
            closeMobileNav?.();
            focusNavToggle();
          }}
        />
      )}
      <AlerteHost />
      <TweaksPanel />
      <KeyboardLayer />
      <OuvrirFenetreDialog />
      <ReceptionComptoir />
      <SuiviSessionEquipe />
    </ShellNavContext.Provider>
  );
}
